/**
 * Экшены модерируемого чата ИИ-матча — приватный слайс matchChat (см.
 * matchChatReducer.js): свои еще не доставленные сообщения и пауза чата.
 *
 * Все строго noServerSync: это память одного клиента, networkMiddleware
 * иначе отзеркалил бы их на сервер через RELAY_ACTION. Само сообщение уходит
 * серверу отдельным сырым WS-сообщением CHAT_SEND, а всем участникам —
 * только после одобрения модератором, обычным ADD_CHAT_MESSAGE с серверной
 * меткой.
 *
 * Лежат не в src/redux/actions.js намеренно: оттуда выводится список
 * экшенов, которые игрок может ретранслировать (redux/relayedActions.js), а
 * этим там не место ни при каких условиях. Типы отличаются от серверных
 * (CHAT_MESSAGE_PENDING и т.п.): сырое серверное сообщение до редьюсеров не
 * доходит, его переводит в экшены applyMatchChatServerMessage.
 */

export const MATCH_CHAT_ACTION_TYPES = {
  SEND_STARTED: 'MATCH_CHAT_SEND_STARTED',
  BLOCKED: 'MATCH_CHAT_MESSAGE_BLOCKED',
  PENDING: 'MATCH_CHAT_MESSAGE_PENDING',
  REJECTED: 'MATCH_CHAT_MESSAGE_REJECTED',
  STATUS_RECEIVED: 'MATCH_CHAT_STATUS_RECEIVED',
  DISMISSED: 'MATCH_CHAT_MESSAGE_DISMISSED',
  EXPIRE_STALE: 'MATCH_CHAT_EXPIRE_STALE',
};

/**
 * id сообщения у отправителя: по нему сервер отвечает PENDING/REJECTED и
 * помечает одобренное ADD_CHAT_MESSAGE, а повторная отправка того же id не
 * создает второго сообщения (см. enqueueChatMessage). Уникален в пределах
 * клиента; случайный хвост — на случай двух вкладок одного игрока.
 */
export const createMatchChatClientMessageId = () => {
  const randomPart = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2, 12);
  return `c-${Date.now().toString(36)}-${randomPart}`;
};

// Сообщение ушло на сервер, ответа еще нет («отправляется…»)
export const matchChatSendStarted = ({clientMessageId, fromPlayerId, fromPlayerName, text, sentAt}) => ({
  type: MATCH_CHAT_ACTION_TYPES.SEND_STARTED,
  payload: {clientMessageId, fromPlayerId, fromPlayerName, text, sentAt},
  noServerSync: true,
});

// Сообщение не ушло вовсе: его отсек бы и сервер (ссылка, секрет, служебные
// символы — см. prefilterChatMessage), показываем отказ сразу
export const matchChatMessageBlocked = ({clientMessageId, fromPlayerId, fromPlayerName, text, reason, blockedAt}) => ({
  type: MATCH_CHAT_ACTION_TYPES.BLOCKED,
  payload: {clientMessageId, fromPlayerId, fromPlayerName, text, reason, blockedAt},
  noServerSync: true,
});

// Сервер принял сообщение в очередь модератора (CHAT_MESSAGE_PENDING)
export const matchChatMessagePending = ({clientMessageId, messageId, receivedAt}) => ({
  type: MATCH_CHAT_ACTION_TYPES.PENDING,
  payload: {clientMessageId, messageId, receivedAt},
  noServerSync: true,
});

// Сообщение не доставлено (CHAT_MESSAGE_REJECTED)
export const matchChatMessageRejected = ({
  clientMessageId, messageId, reason, moderatorReason, retryAfterMs, receivedAt,
}) => ({
  type: MATCH_CHAT_ACTION_TYPES.REJECTED,
  payload: {clientMessageId, messageId, reason, moderatorReason, retryAfterMs, receivedAt},
  noServerSync: true,
});

// Пауза чата (CHAT_STATUS): модератор недоступен — писать нельзя, игра идет
export const matchChatStatusReceived = ({isPaused}) => ({
  type: MATCH_CHAT_ACTION_TYPES.STATUS_RECEIVED,
  payload: {isPaused: Boolean(isPaused)},
  noServerSync: true,
});

// Отправитель закрыл строку отказа крестиком
export const dismissMatchChatMessage = (clientMessageId) => ({
  type: MATCH_CHAT_ACTION_TYPES.DISMISSED,
  payload: {clientMessageId},
  noServerSync: true,
});

// Проверка «зависших» сообщений: ответ сервера потерялся (обрыв связи) —
// см. MATCH_CHAT_SEND_ACK_TIMEOUT_MS и MATCH_CHAT_PENDING_EXPIRY_MS
export const expireStaleMatchChatMessages = (now) => ({
  type: MATCH_CHAT_ACTION_TYPES.EXPIRE_STALE,
  payload: {now},
  noServerSync: true,
});
